import React from 'react';
import { Button } from '@mui/material';
import { Translate } from 'react-localize-redux';
import { analyticsExportFilename } from '../utils/analyticsTableExport';
import { ExportColumn, buildTableCsv, downloadCsv } from '../../../../utils/csvDownload';
import { useAnalyticsExportContext } from './AnalyticsExportContextProvider';

interface ExportTableButtonProps<Row> {
  /**
   * The panel title. It names the FILE — it is not the button's accessible name, which stays
   * the visible "Export" label so that what a screen reader announces matches what is written
   * on the control.
   */
  title: string;
  /** Exactly the rows the table is rendering — see `buildTableCsv` on why that matters. */
  rows: Row[];
  /** Exactly the columns the table is displaying, in display order. */
  columns: ExportColumn<Row>[];
}

/**
 * SHER-905. The export affordance for the "Analyse détaillée" tier of the analytics tabs.
 *
 * GATING. This carries no functionality flag of its own, and that is deliberate. The button
 * only ever exists inside a table, and every analytics table already sits inside a tab gated
 * per tenant by `analyticsConfig.ts` (`FUNCTIONALITY.OUTPATIENTS`, `HOSPITALIZATION`,
 * `SURGERY`, `BILLING`). A tenant without the tab has no table, so no export — the feature is
 * tenant-configurable through the gate that already governs the data it would export. Minting
 * a separate flag would have shipped this dark on every tenant until somebody edited an
 * Investigation row, blocking all five consumer tickets on a database change nobody scheduled.
 *
 * The file is CSV, not xlsx: the client declares no xlsx library, the only existing export in
 * the app is CSV (SHER-462, the surgical waiting list), and the file opens directly in Excel.
 * A real xlsx would mean generating it server-side, which for these tables is not merely more
 * work but wrong — the API cannot read the encrypted patient fields half these columns hold.
 */
function ExportTableButton<Row>({ title, rows, columns }: ExportTableButtonProps<Row>) {
  const hasRows = rows.length > 0;
  // NOT a prop. The active range and service are read from the page once, by
  // AnalyticsExportContextProvider, so that the five tables opting into this export cannot
  // each format the range differently or name a department by its uuid.
  const context = useAnalyticsExportContext();

  const handleExport = () => {
    downloadCsv(analyticsExportFilename(title, context), buildTableCsv(rows, columns));
  };

  return (
    <Button
      size="small"
      variant="outlined"
      color="primary"
      data-testid="analytics-export-csv"
      // Without this the button is the element that gives way when a long French panel title
      // needs the room, and it ends up narrower than its own label.
      sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
      // An empty period is ordinary in these panels, and a file containing nothing but column
      // headings reads as a broken export rather than as an empty period.
      disabled={!hasRows}
      onClick={handleExport}
    >
      <Translate id="hospital.analytics.table.export" />
    </Button>
  );
}

export default ExportTableButton;
